import React from "react";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FileUpload } from "@/Common/Components/FileUpload/FileUpload";
import { AttachDocumentLink } from "@/Common/Components/IconControls/AttachDocumentLink";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { CreatePatientPopup } from "@/Common/Components/Patient/CreatePatientPopup";
import { PdfPageDetails, PdfViewer } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { CliniciansHelper } from "@/Common/Helpers/CliniciansHelper";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { FileType } from "@/Common/Models/FileType";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Server } from "@/Common/Operations/operations.server";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BatchScanPage } from "@/Documents/Api/Dto/Response/BatchScanPage";
import { BatchScanResponse } from "@/Documents/Api/Dto/Response/BatchScanResponse";
import { DocumentUrlAndBool } from "@/Documents/Api/Dto/Response/DocumentUrlAndBool";
import { DocumentEditDetails, DocumentEditDetailsOperations } from "@/Documents/Components/DocumentEdit/DocumentEditDetails";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { LockIconComponent } from "@/Documents/Components/InboundFax/LockIconComponent";
import { MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { IDocumentCreateEditUIModel } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { fetchPrescriptionsRenewalCounters } from "@/PrescriptionRenewals/Api/PrescriptionRenewalsClient";

import "./DocumentCreate.css";

import img_icon_add_details from "@/Globals/_images/icons/documents/add_details.png";
import img_icon_add_details_open from "@/Globals/_images/icons/documents/add_details_open.png";
import img_icon_myle_pictogrammes_documents_32 from "@/Globals/_images/icons/documents/MYLE-Pictogrammes-Documents-32.png";
import img_loading from "@/Globals/_images/loading.gif";

interface IDocumentCreateProps {
    model: IDocumentCreateEditUIModel;
    documentClasses: Immutable.List<DocumentClassModel>;
    sources: Immutable.List<SearchableItemModelT<string>>;
    sites: Immutable.List<SiteUIModel>;
    docDescription: Immutable.List<SearchableItemModelT<string>>;
    beginUpload: boolean;
    isDocumentCreateFromPatient?: boolean;
}

class DocumentCreateState {
    public pdfViewerSelectedZoomLevelIndex: number = 3;
    public patientCreationModel: IPatientCreationModel = Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel());
    public cliniciansSearchStrategy: ClinicianSearchStrategy = new ClinicianSearchStrategy(cacheStore.GetClinicians(false));

    public batchScanPages: BatchScanPage[];
    public selectedPages: PdfPageDetails[] = [];
    public pageDetails: PdfPageDetails[];

    public constructor(init?: Partial<DocumentCreateState>) {
        Object.assign(this, init);
    }
}

class DocumentCreate extends BaseReactComponent<IDocumentCreateProps, DocumentCreateState> {
    private operations: DocumentEditDetailsOperations;
    private fileUpload: FileUpload;
    private input: HTMLInputElement = null;
    private upload_control_id: string = "create_doc_uploader";
    private allCliniciansSearchStrategy = CliniciansHelper.GetAllCliniciansSearchStrategy();

    constructor(props: IDocumentCreateProps) {
        super(props);
        this.state = new DocumentCreateState();

        this.initData();
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        var elem = document.getElementById("doc_create_note");
        autosize(elem);

        const selectedPatient = this.props.model.get("SelectedPatients")?.first();

        if (!!selectedPatient) {
            this.getClinicianSearchStrategy(selectedPatient)
                .then(p => this.mutateState(s => s.cliniciansSearchStrategy = p));
        }

        if (this.props.model.get("BatchScanPages")) {
            this.mutateState(s => {
                s.batchScanPages = this.props.model.get("BatchScanPages");
                s.selectedPages = this.props.model.get("SelectedBatchScanPages");
                s.pageDetails = this.getPagesWithDetails();
            });
        }
    }

    public componentDidUpdate(prevProps: IDocumentCreateProps): void {
        if (this.props.beginUpload) {
            window.setTimeout(
                () => $("#" + this.upload_control_id + "_upload-wrapper").find("button.k-upload-selected").click(),
                0
            );
        }

        const currentSelectedPatient = this.props.model.get("SelectedPatients")?.first();
        const previousSelectedPatient = prevProps.model.get("SelectedPatients")?.first();

        if (this.props.model?.get("RememberClinician") === false) {
            if (!!currentSelectedPatient && currentSelectedPatient?.ContactFacetId !== previousSelectedPatient?.ContactFacetId) {
                this.getClinicianSearchStrategy(currentSelectedPatient)
                    .then(async cliniciansStrategyWithPatientSelected => this.mutateState(s => s.cliniciansSearchStrategy = cliniciansStrategyWithPatientSelected));

                if (this.props.model.get("SelectedClinicians")?.toArray().any() && this.props.model.get("RememberClinician"))
                    return;

                this.autopopulateResponsibleProvider(currentSelectedPatient);
            }
        }
        if ((!this.state.batchScanPages && this.props.model.get("BatchScanPages")) || (this.state.batchScanPages?.any() && this.state.batchScanPages !== this.props.model.get("BatchScanPages"))) {
            this.mutateState(s => {
                s.batchScanPages = this.props.model.get("BatchScanPages");
                s.selectedPages = [];
                s.pageDetails = this.getPagesWithDetails();
            });
        }
        if (this.props.model.get("SelectedBatchScanPages") && this.props.model.get("SelectedBatchScanPages").map(p => p.pageNumber).toString() !== this.state.selectedPages?.map(p => p.pageNumber).toString()) {
            this.mutateState(s => s.selectedPages = this.props.model.get("SelectedBatchScanPages"));
        }
    }

    public componentWillUnmount(): void {
        const event = document.createEvent("Event");
        event.initEvent("autosize:destroy", true, false);
        const elem = document.getElementById("doc_create_note");

        elem?.dispatchEvent(event);

        if (this.hasBatchScan()) {
            sessionStore.dispatchAsync(LocalOperations.Documents.ClearBatchProject);
        }
    }

    public render(): JSX.Element {
        const containerClassName = this.props.isDocumentCreateFromPatient ? "sb-workspace tbc" : "workspace ";
        const headerClassName = this.props.isDocumentCreateFromPatient ? "myle-subheader" : "myle-page-header";
        const footerClassName = this.props.isDocumentCreateFromPatient ? "ws-subfooter" : "ws-footer";
        const footerChildDivClassName = this.props.isDocumentCreateFromPatient ? "no-side-margin" : "";

        return (
            <div className={`${containerClassName} document-create-component`}>
                <div className={`${headerClassName} bottom-border`}>
                    {this.renderHeader()}
                </div>
                {this.props?.isDocumentCreateFromPatient ? (
                    <div className="scrollable" data-hvar="60" style={{ overflow: "hidden auto" }}>
                        <div className="myle-doc-tb main-content myle-form documents document-create-root">
                            {this.renderPopupIfAny()}
                            {this.renderPdfViewer()}
                            {this.renderForm()}
                        </div>
                    </div>
                ) : (
                    <div id="posref" className="ws-scrollable document-create-root">
                        {this.renderPopupIfAny()}
                        {this.renderPdfViewer()}
                        {this.renderForm()}
                    </div>
                )
                }
                <div className={footerClassName}>
                    <div className={`${footerChildDivClassName} row`}>
                        {this.renderFooter()}
                    </div>
                </div>
            </div>
        );
    }

    private renderHeader(): JSX.Element {
        return (
            <div>
                <p className="header-font-color uppercase">{cacheStore.GetString("DocumentsLabel")}</p>
                <h3 className="thin-font">{cacheStore.GetString("AttachmentLabel")}</h3>
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <div className="col-lg-12 text-right">
                <div className="inline-block push-right-10">
                    <div className="checkbox">
                        <label>
                            <input
                                name="urgent"
                                id="urgent"
                                value="urgent"
                                checked={this.getModel().IsUrgent}
                                type="checkbox"
                                onChange={e => this.OnUrgencyCheckChange(e)}
                            />
                            {cacheStore.GetString("UrgentLabel")}
                        </label>
                    </div>
                </div>

                <button
                    onClick={() => this.onCancelClick()}
                    className="btn btn-default push-right-5"
                    data-cy="document-addDoc-discard"
                    disabled={this.props.model.get("LockUI")}
                >
                    {cacheStore.GetString("CancelLabel")}
                </button>

                <button
                    className="btn btn-primary"
                    data-cy="document-addDoc-attach"
                    disabled={this.hasSelectedPages() ? this.isBatchScanningDisabled() : this.isAttachingDisabled()}
                    onClick={e => this.hasSelectedPages() ? this.onBatchScanAttachClick() : this.onAttachDocument(e)}
                >
                    {cacheStore.GetString("AttachLabel")}
                </button>
            </div>
        );
    }

    private renderPdfViewer(): JSX.Element {
        return this.hasBatchScan() && (
            <PdfViewer
                pdfSource={apiSettings.buildAppUrl(FileSelectionHelper.getFetchUrl(this.getBatchScanId(), FileType.BatchScan))}
                selectedPages={this.state?.selectedPages ?? []}
                onPageSelectionChange={selectedPages => this.onPageSelectionChange(selectedPages)}
                pagesWithDetails={this.state.pageDetails}
                onTrashPage={pageDetails => this.updatePageDetails(pageDetails)}
                onPageOrientationChange={pageDetails => this.onPageRotate(pageDetails)}
                selectedZoomLevelIndex={this.state.pdfViewerSelectedZoomLevelIndex}
                onZoomLevelChanged={zoomChange => this.mutateState(s => s.pdfViewerSelectedZoomLevelIndex = zoomChange)}
                onTrashDocument={() => this.openWarningDeletePopup()}
                onClearSelection={() => this.onPageSelectionChange([])}
                onSelectAllSelection={() => this.onPageSelectionChange([...this.getPagesWithDetails().filter(p => !p.isTrashed)])}
                onPopoutPdfPage={pageNumber => this.generatePdfPagePreview(pageNumber)}
                isDeletable
            />
        );
    }

    private renderForm(): JSX.Element {
        return (
            <div className="document-create-form">
                <div className="form-row">

                    <label className="form-label">{cacheStore.GetString("PatientLabel")}</label>

                    <PersonSearchAutocompleteCustom
                        valid={this.props.model.get("SelectedPatients").size > 0}
                        useBlurEvent={false}
                        focusOnLoad={true}
                        disabled={this.props.model.get("LockUI") || !!this.props.isDocumentCreateFromPatient}
                        componentId="add_doc_patients"
                        dataCy="document-addDoc-patients"
                        selectedItemList={this.props.model.get("SelectedPatients")}
                        selectionOp={LocalOperations.Documents.Create_Patient_Selection_Change}
                        clearOp={LocalOperations.Documents.Create_Patient_ClearSelection}
                        closeDropDownAfterSelection={true}
                    />

                    <LockIconComponent
                        isLocked={this.props.model.get("RememberPatient")}
                        onChange={() => this.onToggleRememberPatient()}
                    />

                    {this.props.model.get("SelectedPatients").size == 0 && cacheStore?.ClinicSettings.EnablePatientEdit && (
                        <span className="link-wrapper">
                            <a onClick={() => this.createNewPatientPopup()}>{cacheStore.GetString("CreatePatientFileLabel")}</a>
                        </span>
                    )}

                </div>

                <div className="form-row">

                    <label className="form-label">{cacheStore.GetString("ResponsibleProvidersLabel")}</label>

                    <PersonSearchAutocompleteCustom
                        disabled={this.props.model.get("LockUI")}
                        componentId="add_doc_clinicians"
                        dataCy="document-addDoc-clinicians"
                        selectedItemList={this.props.model.get("SelectedClinicians")}
                        focusOnLoad={false}
                        closeDropDownAfterSelection={false}
                        searchStrategy={this.state.cliniciansSearchStrategy}
                        allowMultipleItemSelection={true}
                        canCloseDropdownAfterAllItemsSelected={true}
                        onSearch={(searchText: string) => {
                            if (this.props.model.get("SelectedPatients")?.first() && IsNullOrWhiteSpace(searchText) &&
                                !CommonHelper.hasAllPerson(
                                    this.state.cliniciansSearchStrategy.cliniciansToSearchFrom,
                                    (this.props.model.get("SelectedClinicians")?.toArray() ?? [])
                                )
                            ) {
                                return this.state.cliniciansSearchStrategy.executeSearch(null);
                            }
                            else {
                                return this.allCliniciansSearchStrategy.executeSearch(searchText);
                            }
                        }}
                        clearOp={LocalOperations.Documents.Create_Clinician_Selection_Change}
                        selectionOp={LocalOperations.Documents.Create_Clinician_Selection_Change}
                    />

                    <LockIconComponent
                        isLocked={this.props.model.get("RememberClinician")}
                        onChange={() => this.onToggleRememberClinicians()}
                    />
                </div>

                {this.props.sites.size > 1 &&
                    (
                        <div className="form-row">

                            <label className="form-label">{cacheStore.GetString("SiteDLabel")}</label>

                            <select className="form-control myle-control minimal" data-cy="document-addDoc-sites" onChange={e => this.OnSiteSelectedChange(e)} value={this.getModel().SiteId}>
                                {this.props.sites.map((site: SiteUIModel) => (
                                    <option key={site.Id} value={site.Id}>{site.Name}</option>
                                ))}
                            </select>

                            <LockIconComponent
                                isLocked={this.props.model.get("RememberSite")}
                                onChange={() => this.onToggleRememberSite()}
                            />
                        </div>
                    )
                }

                <div className="form-row">

                    <label className="form-label">{cacheStore.GetString("ClassLabel")}</label>

                    <select
                        className="form-control myle-control minimal"
                        data-cy="document-addDoc-classes"
                        disabled={this.props.model.get("LockUI")}
                        onChange={e => this.OnClassSubClassSelectionChange(e)}
                        value={this.getSelectedClassAndSubClass()}
                    >
                        {this.props.documentClasses.toArray().map(g => (
                            <optgroup key={g.LookupId} label={cacheStore.GetLocalizedValueByLookupId(g.LookupId)}>
                                {g.SubClasses.toArray().map(s => (
                                    <option
                                        key={s.LookupId}
                                        value={JSON.stringify({ class: g.LookupId.toLocaleLowerCase(), subclass: s.LookupId.toLocaleLowerCase() })}
                                    >
                                        {cacheStore.GetLocalizedValueByLookupId(g.LookupId) + " - " + cacheStore.GetLocalizedValueByLookupId(s.LookupId)}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>

                </div>

                <div className="form-row">

                    <label className="form-label">{cacheStore.GetString("SourceLabel")}</label>

                    <TextboxControl
                        dataCy="document-addDoc-source"
                        disabled={this.props.model.get("LockUI")}
                        autoCompleteItems={this.props.sources}
                        value={this.props.model.get("Source")}
                        operation={LocalOperations.Documents.Create_OnSourcesChange}
                        onAutoCompleteSelectCallBackOperation={LocalOperations.Documents.Create_OnSourcesChange}
                        className="form-control"
                        RemoveStyle
                    />

                </div>
                <div className="form-row">

                    <label className="form-label">{cacheStore.GetString("DescriptionDLabel")}</label>

                    <TextboxControl
                        dataCy="document-addDoc-description"
                        disabled={this.props.model.get("LockUI")}
                        autoCompleteItems={this.props.docDescription}
                        value={this.props.model.get("Description")}
                        onAutoCompleteSelectCallBackOperation={LocalOperations.Documents.Create_OnDescriptionChange}
                        operation={LocalOperations.Documents.Create_OnDescriptionChange}
                        className="form-control"
                        RemoveStyle
                    />

                    <LockIconComponent
                        isLocked={this.props.model.get("RememberDescription")}
                        onChange={() => this.onToggleRememberDescription()}
                    />
                </div>

                <div className="form-row">

                    <label className="form-label">{cacheStore.GetString("DocumentDateLabel")}</label>

                    <DateSelector
                        id="doc_create_date"
                        date={this.props.model.get("DocumentDate")}
                        onSelectOp={LocalOperations.Documents.Create_Set_Document_Date}
                        dateFormat="dddd, dd MMMM yyyy"
                        disabled={this.props.model.get("LockUI")}
                    />
                    <span className="today-link-wrapper">
                        <a className="today-link" id="dp1-today" onClick={e => this.onTodayClick(e)}>{cacheStore.GetString("TodayLabel")}</a>
                    </span>
                </div>
                {!this.hasBatchScan() && (
                    <div className="form-row three-col">
                        <label className="form-label">{cacheStore.GetString("DocumentGridFilesLabel")}</label>
                        <div>
                            <AttachDocumentLink onClick={() => this.SelectFiles()} />
                        </div>
                        {!this.props.isDocumentCreateFromPatient &&
                            this.renderBatchScanningButton()
                        }

                        <FileUpload
                            ref={ref => this.fileUpload = ref}
                            componentId={this.upload_control_id}
                            onErrorOperation={LocalOperations.Documents.Create_UploadFailPatientProfile}
                            onFileSelectionCallback={LocalOperations.Documents.Create_OnFileSelectionChange}
                            className="file-upload"
                            onCompleted={async () => {
                                sessionStore.dispatch(LocalOperations.Documents.Create_UploadComplete);
                                if (this.isPrescriptionRenewalEnable())
                                    fetchPrescriptionsRenewalCounters();
                            }}
                        />
                    </div>
                )}
                <div className="form-row">
                    <span className="link-wrapper">
                        <a
                            href="#"
                            className="marginleft5 pointer additional_details_link show_additional_details_bubble"
                            id="show_additional_details"
                            onClick={e => this.ShowAdditionalDetails(e)}
                        />
                        {this.renderDocumentDetailsExtender()}
                        <a className="marginleft5" data-cy="document-addDoc-showAdditionalDetails" onClick={e => this.ShowAdditionalDetails(e)}>{this.getDocumentDetailsLabel()}</a>
                    </span>
                </div>

                {this.props.model.get("AdditionalDetailsVisible") && (
                    <div className="form-row">
                        <span className="link-wrapper">
                            <DocumentEditDetails
                                model={this.props.model}
                                operations={this.operations}
                                disabled={this.props.model.get("LockUI")}
                                className="document-details"
                            />
                        </span>
                    </div>
                )}

                <div className="form-row">
                    <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                    <textarea className="form-control" onChange={e => this.OnNoteChange(e)} id="doc_create_note" value={this.props.model.get("Note")} disabled={this.props.model.get("LockUI")} rows={4} />
                </div>
            </div>
        );
    }

    private renderPopupIfAny(): JSX.Element {
        if (this.props.model.get("BatchScanPopupOpen")) {
            return (
                <Popup cssClass="double-booking" title={cacheStore.GetString("WarningLabel")} onCloseDelegate={() => this.closeBatchScanPopup()}>
                    <div className="p-body">
                        <label>{JSXHelper.BreakStringIntoParagraphs(cacheStore.GetString("BatchErrorMessageLabel"))}</label>
                    </div>
                    <div className="p-footer">
                        <div className="text-right">
                            <button className="btn btn-default push-right-5" onClick={() => this.closeBatchScanPopup()}>{cacheStore.GetString("CancelLabel")}</button>
                            <button className="btn btn-danger push-right-5" onClick={() => this.onStartANewProjectClick()}>{cacheStore.GetString("StartNewProjectLabel")}</button>
                            <button className="btn btn-primary" onClick={() => this.continueProjectClick()}>{cacheStore.GetString("ContinueExistingProjectLabel")}</button>
                        </div>
                    </div>
                </Popup>
            );
        }

        else if (this.props.model.get("StartNewPojectPopup")) {
            return (
                <Popup cssClass="double-booking" title={cacheStore.GetString("WarningLabel")} onCloseDelegate={() => this.onStartANewProjectCancelClick()}>
                    <div className="p-body">
                        <p>
                            <label>{cacheStore.GetString("StartNewProjectWarning1Label")}</label>
                        </p>
                        <p>
                            <strong className="red-text" style={{ textDecoration: "underline" }}>{cacheStore.GetString("StartNewProjectWarning2Label")}</strong>
                        </p>
                        <p>
                            <label>{cacheStore.GetString("StartNewProjectWarning3Label")}</label>
                        </p>
                    </div>
                    <div className="p-footer">
                        <div className="text-right">
                            <button className="btn btn-default push-right-5" onClick={() => this.onStartANewProjectCancelClick()}>{cacheStore.GetString("CancelLabel")}</button>
                            <button className="btn btn-danger" onClick={() => this.onStartANewProjectContinueClick()}>{cacheStore.GetString("ContinueLabel")}</button>
                        </div>
                    </div>
                </Popup>
            );
        }

        else if (this.props.model.get("PleaseWaitPopup")) {
            return (
                <Popup cssClass="double-booking" title={cacheStore.GetString("UploadingLabel")}>
                    <div className="p-body">
                        <div>
                            <strong>{cacheStore.GetString("RequestIsBeingProceedLabel")}</strong>
                            <div>
                                <img src={img_loading} alt="loading" />
                            </div>
                        </div>
                    </div>
                </Popup>
            );
        }

        else if (this.props.model.get("DeleteWarningPopup")) {
            return (
                <Popup cssClass="double-booking" title={cacheStore.GetString("WarningLabel")} onCloseDelegate={() => this.closeWarningDeletePopup()}>
                    <div className="p-body">
                        <p>
                            <label>{cacheStore.GetString("DeleteBatchScanWarning1Label")}</label>
                        </p>
                        <p>
                            <strong className="red-text" style={{ textDecoration: "underline" }}>{cacheStore.GetString("DeleteBatchScanWarning2Label")}</strong>
                        </p>
                        <p>
                            <label>{cacheStore.GetString("DeleteBatchScanWarning3Label")}</label>
                        </p>
                    </div>
                    <div className="p-footer">
                        <div className="text-right">
                            <button className="btn btn-default push-right-5" onClick={() => this.closeWarningDeletePopup()}>{cacheStore.GetString("CancelLabel")}</button>
                            <button className="btn btn-danger" onClick={() => this.deleteFileClick()}>{cacheStore.GetString("DeleteFileLabel")}</button>
                        </div>
                    </div>
                </Popup>
            );
        }

        else if (this.props.model.get("ShowCreatePatientPopup")) {
            return (
                <CreatePatientPopup
                    patientCreationModel={this.state?.patientCreationModel}
                    onSave={patient => this.createNewPatient(patient)}
                    onCancel={() => this.onCancelButtonClick()}
                />
            );
        }

        return null;
    }

    private renderBatchScanningButton(): JSX.Element {
        return (
            <div>
                <img
                    className="marginright5 image-batch"
                    src={img_icon_myle_pictogrammes_documents_32}
                    alt={cacheStore.GetString("BatchScanningLabel")}
                    onClick={() => this.startBatchScan()}
                />
                <a className="link-label label-maring-top" onClick={() => this.startBatchScan()}>
                    {cacheStore.GetString("BatchScanningLabel")}
                </a>
            </div>
        );
    }

    private async startBatchScan(): Promise<void> {
        const batchScanPages = await this.getBatchScanPagesInfo();
        batchScanPages?.any(p => !p.IsIndexed) ? this.openBatchScanPopup() : this.openUploadFilePopup();
    }

    private async getBatchScanPagesInfo(): Promise<BatchScanPage[]> {
        const response = await Client.postAsync<BatchScanResponse>(
            Server.Myle_MedicalDocuments_GetBatchScanPagesInfo,
            { EmployeeId: sessionStore.getCurrentEmployee().Id }
        );

        if (response.isSuccess) {
            return response.data.BatchScanPages;
        }

        return null;
    }

    private openUploadFilePopup(): void {
        this.input?.remove();
        this.input = document.createElement("input");
        this.input.type = "file";
        this.input.addEventListener("change", () => this.setSelectedFile());
        this.input.click();
    }

    private setSelectedFile(): void {
        const files = Array.from(this.input.files);
        const newFilesSize = files.reduce((sum, current) => sum + current.size, 0);
        if (newFilesSize > cacheStore.BatchScanFileMaximum) {
            sessionStore.showPopup({
                title: cacheStore.GetString("ErrorLabel"),
                content: [`${cacheStore.GetString("ExceedTheMaximumSizeAllowedLabel")} (${(cacheStore.BatchScanFileMaximum / 1000000)}MB)`],
                autoWidth: true
            });

            return;
        }
        else {
            this.uploadFile(this.input.files[0]);
        }

        this.input.remove();
    }

    private OnClassSubClassSelectionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var o = JSON.parse(e.target.value);
        var operation = new LocalEvent(LocalOperations.Documents.Create_SubClassChange);
        operation.AddItem("value_class", o.class);
        operation.AddItem("value_subclass", o.subclass);
        LocalEventDispatcher.dispatch(operation);
    }

    private uploadFile(file: File): void {
        var ajaxRequest = new XMLHttpRequest();
        ajaxRequest.withCredentials = true;

        ajaxRequest.addEventListener("load", () => {
            var response: IBatchUploadResponse = JSON.parse(ajaxRequest.responseText);
            if (response.Success) {
                sessionStore.dispatch(LocalOperations.Documents.FinishBatchScan, { BatchId: response.BatchId, BatchScanPages: response.BatchScanPages });
            }
            else {
                let error = response.Errors && response.Errors.firstOrDefault();
                this.handleUploadException(error);
            }
        });

        ajaxRequest.open("post", apiSettings.buildAppUrl("api/medicalDocuments/uploadBatch.json"), true);
        ajaxRequest.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
        ajaxRequest.setRequestHeader("X-File-Size", String(file.size));
        ajaxRequest.setRequestHeader("X-File-Type", file.type);
        ajaxRequest.setRequestHeader("api-key", apiSettings.ApiKey);
        ajaxRequest.setRequestHeader("sessionId", apiSettings.SessionId);
        ajaxRequest.setRequestHeader("employeeId", sessionStore.getCurrentEmployee().ContactId);
        ajaxRequest.onerror = () => this.handleUploadException("Error while sending request.");
        ajaxRequest.send(file);
    }

    private handleUploadException(message: string): void {
        sessionStore.dispatch(LocalOperations.Documents.closePleaseWaitPopup);
        sessionStore.showPopup(message || "Unknown Error.");
    }

    private ShowAdditionalDetails(e: React.MouseEvent): void {
        e.preventDefault();
        var operation = new LocalEvent(LocalOperations.Documents.Create_ShowDetails);
        LocalEventDispatcher.dispatch(operation);
    }

    private onAttachDocument(e: React.MouseEvent): void {
        e.preventDefault();

        if (this.isAttachingDisabled())
            return;

        sessionStore.dispatch(LocalOperations.Documents.Create_OnAttachClick);
    }

    private onCancelClick(): void {
        var operation = new LocalEvent(LocalOperations.Documents.Create_OnCancel);
        LocalEventDispatcher.dispatch(operation);
    }

    private onTodayClick(e: React.MouseEvent): void {
        e.preventDefault();
        var operation = new LocalEvent(LocalOperations.Documents.Create_OnTodayClick);
        LocalEventDispatcher.dispatch(operation);
    }

    private OnUrgencyCheckChange(e: React.ChangeEvent<HTMLInputElement>): void {
        var operation = new LocalEvent(LocalOperations.Documents.Create_OnUrgencyCheckChange);
        operation.AddItem("value", e.target.checked);
        LocalEventDispatcher.dispatch(operation);
    }

    private OnSiteSelectedChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var operation = new LocalEvent(LocalOperations.Documents.Create_SiteChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private OnNoteChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        var operation = new LocalEvent(LocalOperations.Documents.Create_NoteChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    private SelectFiles(): void {
        this.fileUpload?.openUpload();
    }

    private closeBatchScanPopup(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.CloseBatchScanPopup);
    }

    private onStartANewProjectClick(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.StartANewProjectClick);
    }

    private onStartANewProjectCancelClick(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.StartANewProjectCancelClick);
    }

    private onStartANewProjectContinueClick(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.StartANewProjectContinueClick);

        this.openUploadFilePopup();
    }

    private openBatchScanPopup(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.OpenBatchScanPopup);
    }

    private continueProjectClick(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.ContinueProjectClick);
    }

    private deleteFileClick(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.DeleteFileClick);
    }

    private onPageSelectionChange(selectedPages: PdfPageDetails[]): void {
        sessionStore.dispatch(
            LocalOperations.Documents.OnPageClick,
            { selectedPages }
        );
    }

    private onBatchScanAttachClick(): void {
        if (!this.isBatchScanningDisabled())
            sessionStore.dispatch(LocalOperations.Documents.OnBatchScanAttachClick);
    }

    private openWarningDeletePopup(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.OpenWarningDeletePopup);
    }

    private closeWarningDeletePopup(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.CloseWarningDeletePopup);
    }

    private onToggleRememberDescription(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.ToggleRememberDescription);
    }

    private onToggleRememberClinicians(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.ToggleRememberClinician);
    }

    private onToggleRememberSite(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.ToggleRememberSite);
    }

    private onToggleRememberPatient(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.ToggleRememberPatient);
    }

    private createNewPatientPopup(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.OnCreateNewPatientClick);
    }

    private onCancelButtonClick(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.CloseCreatePatientPopup);
    }

    private onPageRemoveClick(page: Partial<DocumentUrlAndBool>, e: React.MouseEvent): void {
        e?.stopPropagation();

        var op = new LocalEvent(LocalOperations.Documents.OnRemovePageClick);
        op.AddItem("pageSize", page.PageSizes);
        op.AddItem("pageNumber", page.PageNumber);
        LocalEventDispatcher.dispatch(op);
    }

    private onPageRotate(pageDetails: PdfPageDetails): void {
        sessionStore.dispatch(
            LocalOperations.Documents.OnRotatePage,
            { pageNumber: pageDetails.pageNumber, newOrientation: pageDetails.orientation }
        );
    }

    private async getClinicianSearchStrategy(patient: Person): Promise<ClinicianSearchStrategy> {
        return await CliniciansHelper.getSortedPatientClinicianSearchStrategy(patient, true);
    }

    private initData(): void {
        this.operations = new DocumentEditDetailsOperations();
        this.operations.AuthroingDateChangeOperation = LocalOperations.Documents.Create_AuthoringDateChange;
        this.operations.SendingFacilityIdChangeOperation = LocalOperations.Documents.Create_SendingFacilityIdChange;
        this.operations.MnemonicChangeOperation = LocalOperations.Documents.Create_MnemonicChange;
        this.operations.SourceAuthorNameChangeOperation = LocalOperations.Documents.Create_SourceAuthorNameChange;
        this.operations.ObservationDateChangeOperation = LocalOperations.Documents.Create_ObservationDateChange;
        this.operations.SendingFacilityReportChangeOperation = LocalOperations.Documents.Create_SendingFacilityReportChange;
        this.operations.SubClassChangeOperation = LocalOperations.Documents.Create_AccompanyingSubClassChange;
        this.operations.HRMStatusChangeOperation = LocalOperations.Documents.Create_HRMStatusChange;
    }

    private hasBatchScan(): boolean {
        return !this.props.isDocumentCreateFromPatient && this.state.batchScanPages?.any();
    }

    private getBatchScanId(): string {
        return `${this.state.batchScanPages?.firstOrDefault().BatchId}`;
    }

    private getPagesWithDetails(): PdfPageDetails[] {
        const batchScanPages = this.state.batchScanPages ?? this.props.model.get("BatchScanPages");
        return batchScanPages?.map(page => ({
            isAssigned: false,
            isTrashed: page.IsIndexed,
            orientation: page.Rotation,
            pageNumber: page.PageNumber
        })) ?? [];
    }

    private async updatePageDetails(pageDetails: PdfPageDetails): Promise<void> {
        if (pageDetails.isTrashed)
            this.onPageRemoveClick({ PageNumber: pageDetails.pageNumber }, null);
    }

    private hasSelectedPages(): boolean {
        return this.props.model.get("SelectedBatchScanPages")?.any() ?? false;
    }

    private async createNewPatient(patientModel: IPatientCreationModel): Promise<void> {
        const response = await InboundFaxesMailboxActions.createPatient(patientModel);

        if (response.Individual) {
            LocalEvent.Dispatch(LocalOperations.Documents.CloseCreatePatientPopup);
            const operation = new LocalEvent(LocalOperations.Documents.Save_NewPatient);
            operation.AddItem("new_patient", Person.FromIndividual(response.Individual));
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private getDocumentDetailsLabel(): string {
        return this.props.model.get("AdditionalDetailsVisible")
            ? cacheStore.GetString("HideDocumentDetailsLabel")
            : cacheStore.GetString("AddDocumentDetailsLabel");
    }

    private getSelectedClassAndSubClass(): string {
        return JSON.stringify({ class: this.props.model.get("ClassLookupId"), subclass: this.props.model.get("SubClassLookupId") });
    }

    private renderDocumentDetailsExtender(): JSX.Element {
        return this.props.model.get("AdditionalDetailsVisible")
            ? <img src={img_icon_add_details_open} alt={cacheStore.GetString("AddDocumentDetailsLabel")} onClick={e => this.ShowAdditionalDetails(e)} />
            : <img src={img_icon_add_details} alt={cacheStore.GetString("AddDocumentDetailsLabel")} onClick={e => this.ShowAdditionalDetails(e)} />;
    }

    private getModel(): MedicalDocumentItemDto {
        return this.props.model.get("MedicalDocumentInfo").Model;
    }

    private isAttachingDisabled(): boolean {
        return (
            this.props.model.get("IsSaving")
            || this.props.model.get("LockUI")
            || !this.props.model.get("SelectedPatients").size
        )
            || !this.props.model.get("NumberOfFiles");
    }

    private isBatchScanningDisabled(): boolean {
        return this.props.model.get("LockUI") || !this.props.model.get("SelectedPatients").size || (!this.props.model.get("SelectedBatchScanPages")?.any() ?? true);
    }

    private generatePdfPagePreview(pageNumber: number): void {
        var op = new LocalEvent(LocalOperations.Documents.OnEnlargePageClick);
        op.AddItem("value", pageNumber);
        LocalEventDispatcher.dispatch(op);
    }

    private async autopopulateResponsibleProvider(currentSelectedPatient: Person): Promise<void> {
        const selectedClinicians: Readonly<Immutable.List<Person> | Person[]> = this.props.model?.get("SelectedClinicians") ?? [];
        const responsibleProviderToAutopopulate: Person = await CliniciansHelper.getStarredAndRamqRegisteredClinicians(currentSelectedPatient);
        const isClinicianAlreadySelected = selectedClinicians.some((p: Person) => p.ContactFacetId === responsibleProviderToAutopopulate.ContactFacetId);

        if (responsibleProviderToAutopopulate && !isClinicianAlreadySelected) {
            LocalEvent.Dispatch(LocalOperations.Documents.Create_Clinician_Selection_Change, { value: { Item: responsibleProviderToAutopopulate } });
        }
    }

    private isPrescriptionRenewalEnable(): boolean {
        return (
            cacheStore.ClinicSettings.EnablePrescriptionRenewal &&
            this.props.model.get("ClassLookupId") ===
            cacheStore.FindLookupByLookupCode(
                DocumentClass.MedicationAndPrescriptions
            ).LookupID
        );
    }
}

interface IBatchUploadResponse {
    Path: string;
    Success: boolean;
    BatchId: string;
    Errors: string[];
    BatchScanPages: BatchScanPage[];
}

export { DocumentCreate, IDocumentCreateProps };
